import React from 'react';
import styled from 'styled-components';
import { Button, COLOR_SYSTEM, Text, Spinner, Icon } from 'components/atoms';
import { StepData, ConfigurationData } from './index';
import { PROMPT_TYPE_KOREAN_NAMES } from 'common/models/types';

interface ExperimentStep7Props {
  data: StepData;
  configurations: ConfigurationData[];
  setConfigurations: React.Dispatch<React.SetStateAction<ConfigurationData[]>>;
  isAddingConfig: boolean;
  onAddConfiguration: () => void;
  onExecute: () => void;
  onBack: () => void;
  isLoading?: boolean;
}

const ExperimentStep7: React.FC<ExperimentStep7Props> = ({
  data,
  configurations,
  setConfigurations,
  isAddingConfig,
  onAddConfiguration,
  onExecute,
  onBack,
  isLoading = false,
}) => {
  const toggleExpand = (id: string) => {
    setConfigurations(prev => prev.map(c => 
      c.id === id ? { ...c, isExpanded: !c.isExpanded } : c
    ));
  };
  
  const deleteConfiguration = (id: string) => {
    setConfigurations(prev => prev.filter(c => c.id !== id));
  };
  
  const calculateTotalCount = () => {
    let count = 0;
    
    configurations.forEach(c => {
      const config = c.config;
      let configCount = 0;
      if (config.patientIds?.length) configCount += config.patientIds.length;
      if (config.aiReviewIds?.length) configCount += config.aiReviewIds.length;
      if (config.aiReviewEvaluationIds?.length) configCount += config.aiReviewEvaluationIds.length;
      count += configCount * (config.iterations || 1);
    });
    
    return count;
  };
  
  const getModeText = (config: any) => {
    const modes = [];
    if (config.patientIds?.length) modes.push('AI Review 생성+평가');
    if (config.aiReviewIds?.length) modes.push('기존 AI Review 평가');
    if (config.aiReviewEvaluationIds?.length) modes.push('재평가');
    return modes.join(', ');
  };
  
  return (
    <Container>
      <StepTitle>실험 설정 확인</StepTitle>
      <StepDescription>
        아래 설정으로 실험을 시작합니다. 여러 테스트 구성을 추가하거나 편집할 수 있습니다.
      </StepDescription>
      
      <SummaryContainer>
        <SummarySection>
          <SectionTitle>기본 정보</SectionTitle>
          <SummaryItem>
            <ItemLabel>실험명:</ItemLabel>
            <ItemValue>{data.name || '자동 생성'}</ItemValue>
          </SummaryItem>
          <SummaryItem>
            <ItemLabel>설명:</ItemLabel>
            <ItemValue>{data.description || '없음'}</ItemValue>
          </SummaryItem>
        </SummarySection>
        
        <SummarySection>
          <SectionHeader>
            <SectionTitle>테스트 구성 ({configurations.length}개)</SectionTitle>
            <Button
              size="Small"
              type="Text"
              variant="Ghost"
              color="Primary"
              onClick={onAddConfiguration}
            >
              <Icon name="Plus" size={16} /> 추가
            </Button>
          </SectionHeader>
          
          {configurations.length === 0 && (
            <EmptyMessage>
              테스트 구성을 추가하려면 위의 추가 버튼을 클릭하세요.
            </EmptyMessage>
          )}
          
          {configurations.map((configData, index) => (
            <ConfigCard key={configData.id}>
              <ConfigHeader>
                <ConfigTitle>테스트 구성 #{index + 1}</ConfigTitle>
                <ConfigActions>
                  <IconButton onClick={() => toggleExpand(configData.id)}>
                    <Icon 
                      name={configData.isExpanded ? "Chevron Up" : "Chevron Down"} 
                      size={20} 
                    />
                  </IconButton>
                  <IconButton onClick={() => deleteConfiguration(configData.id)}>
                    <Icon name="X" size={20} />
                  </IconButton>
                </ConfigActions>
              </ConfigHeader>
              
              <ConfigSummary>
                <ConfigItem>
                  <ConfigLabel>모드:</ConfigLabel>
                  <ConfigValue>{getModeText(configData.config)}</ConfigValue>
                </ConfigItem>
                <ConfigItem>
                  <ConfigLabel>프롬프트:</ConfigLabel>
                  <ConfigValue>
                    {configData.config.aiReviewRequest.promptType && 
                      PROMPT_TYPE_KOREAN_NAMES[configData.config.aiReviewRequest.promptType]}
                  </ConfigValue>
                </ConfigItem>
                <ConfigItem>
                  <ConfigLabel>반복:</ConfigLabel>
                  <ConfigValue>{configData.config.iterations}회</ConfigValue>
                </ConfigItem>
              </ConfigSummary>
              
              {configData.isExpanded && (
                <ConfigDetails>
                  <DetailSection>
                    <DetailTitle>AI Review 설정</DetailTitle>
                    <DetailItem>모델: {configData.config.aiReviewRequest.modelType}</DetailItem>
                    <DetailItem>프롬프트 ID: {configData.config.aiReviewRequest.promptId}</DetailItem>
                    <DetailItem>
                      약물 옵션: {configData.config.aiReviewRequest.medicationOptions?.length || 0}개
                    </DetailItem>
                    <DetailItem>
                      문서 옵션: {configData.config.aiReviewRequest.docsOptions?.length || 0}개
                    </DetailItem>
                  </DetailSection>
                  
                  <DetailSection>
                    <DetailTitle>평가 설정</DetailTitle>
                    <DetailItem>
                      평가 프롬프트 ID: {configData.config.aiReviewRequest.evaluationPromptId}
                    </DetailItem>
                    <DetailItem>
                      평가 모델: {configData.config.aiReviewRequest.evaluationModel}
                    </DetailItem>
                    {configData.config.aiReviewRequest.evaluationUseGTSnapshot && (
                      <DetailItem>GT Snapshot 사용</DetailItem>
                    )}
                  </DetailSection>
                  
                  <DetailSection>
                    <DetailTitle>실행 대상</DetailTitle>
                    {configData.config.patientIds?.length > 0 && (
                      <DetailItem>환자: {configData.config.patientIds.length}명</DetailItem>
                    )}
                    {configData.config.aiReviewIds?.length > 0 && (
                      <DetailItem>AI Review: {configData.config.aiReviewIds.length}개</DetailItem>
                    )}
                    {configData.config.aiReviewEvaluationIds?.length > 0 && (
                      <DetailItem>평가 결과: {configData.config.aiReviewEvaluationIds.length}개</DetailItem>
                    )}
                  </DetailSection>
                </ConfigDetails>
              )}
            </ConfigCard>
          ))}
        </SummarySection>
      </SummaryContainer>
      
      <TotalCountBox>
        <TotalCountLabel>총 실행 수</TotalCountLabel>
        <TotalCountValue>{calculateTotalCount()}개</TotalCountValue>
      </TotalCountBox>
      
      <ButtonGroup>
        <Button
          size="Medium"
          type="Text"
          variant="Ghost"
          color="Secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          이전
        </Button>
        <Button
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={onExecute}
          disabled={isLoading || configurations.length === 0}
          style={{ minWidth: '120px' }}
        >
          {isLoading ? <Spinner size="Small" /> : '실험 시작'}
        </Button>
      </ButtonGroup>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const StepDescription = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.5;
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  margin-bottom: 4px;
`;

const SummaryItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ItemLabel = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  min-width: 120px;
`;

const ItemValue = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 500;
`;

const EmptyMessage = styled(Text)`
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  padding: 40px;
`;

const ConfigCard = styled.div<{ $isActive?: boolean }>`
  padding: 16px;
  background-color: ${({ $isActive }) => 
    $isActive ? COLOR_SYSTEM.get("Blue")?.[50] : 'white'};
  border: 1px solid ${({ $isActive }) => 
    $isActive ? COLOR_SYSTEM.get("Blue")?.[300] : COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const ConfigHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ConfigTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const ConfigActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  border-radius: 4px;
  
  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const ConfigSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const ConfigItem = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const ConfigLabel = styled(Text)`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const ConfigValue = styled(Text)`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 500;
`;

const ConfigDetails = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin-bottom: 4px;
`;

const DetailItem = styled(Text)`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  padding-left: 12px;
`;

const TotalCountBox = styled.div`
  padding: 20px;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border: 2px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
  border-radius: 8px;
  text-align: center;
`;

const TotalCountLabel = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Blue")?.[700]};
  margin-bottom: 8px;
  display: block;
`;

const TotalCountValue = styled(Text)`
  font-size: 28px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Blue")?.[900]};
  display: block;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

export default ExperimentStep7;